import React, { useRef } from 'react';
import { Group } from 'react-konva';
import { distance, calculatePosition } from 'src/util/math';
import Grabber from '../../grabber';
import type { KonvaEventObject } from 'konva/lib/Node';
import { CylinderType } from 'src/types/transforms';
import { useDragGesture } from 'src/hooks/useDragGesture';

type Point = {
    x: number;
    y: number;
};

type Props = {
    container?: HTMLElement;
    scaledCylinderWarp: Omit<CylinderType, 'id' | 'fullWrap'>;
    cylinderWarp: CylinderType;
    scale: number;
    onDragMove: (warp: CylinderType) => void;
    onDragEnd: () => void;
    cursorChange: (cursor: string) => void;
    offset: Point;
};

const RadiusGrabbers: React.FC<Props> = (props) => {
    const {
        container,
        scaledCylinderWarp,
        cylinderWarp,
        scale,
        onDragMove: onDragMoveProp,
        onDragEnd,
        cursorChange,
        offset,
    } = props;

    const startCylinderWarpRef = useRef<CylinderType | null>(null);
    // Which grabber (top/bottom radiusX vs. radiusY) is being dragged -- set once at drag-start,
    // read by the single `handleDragMove` for the duration of that gesture.
    const dragKindRef = useRef({ top: false, y: false });

    const handleDragMove = (e: MouseEvent): void => {
        const { top, y } = dragKindRef.current;
        const warp = JSON.parse(JSON.stringify(startCylinderWarpRef.current));

        const containerRect = container?.getBoundingClientRect();
        const mousePosition: Point = {
            x: (e.clientX - containerRect!.left - offset.x) / scale,
            y: (e.clientY - containerRect!.top - offset.y) / scale,
        };

        if (!y && top) {
            warp.cylinderTop.radiusX = distance(warp.cylinderTop.center, mousePosition);
        } else if (!y) {
            warp.cylinderBottom.radiusX = distance(warp.cylinderBottom.center, mousePosition);
        } else {
            const ellipse = top ? warp.cylinderTop : warp.cylinderBottom;
            ellipse.radiusY = distance(ellipse.center, mousePosition);

            const ptAboveDistance = distance(calculatePosition(ellipse, 270), mousePosition);
            const ptBellowDistance = distance(calculatePosition(ellipse, 90), mousePosition);

            if (ptAboveDistance < ptBellowDistance) {
                ellipse.radiusY = -ellipse.radiusY;
            }
        }

        onDragMoveProp(warp);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onDragStart = (e: KonvaEventObject<globalThis.MouseEvent>, top: boolean, y: boolean): void => {
        startCylinderWarpRef.current = cylinderWarp;
        dragKindRef.current = { top, y };

        start();
        e.cancelBubble = true;
    };

    const topRight = calculatePosition(scaledCylinderWarp.cylinderTop, 0);
    const bottomRight = calculatePosition(scaledCylinderWarp.cylinderBottom, 0);
    const topBottom = calculatePosition(scaledCylinderWarp.cylinderTop, 90);
    const bottomBottom = calculatePosition(scaledCylinderWarp.cylinderBottom, 90);

    return (
        <Group>
            <Grabber position={topRight} cursorChange={cursorChange} onDragStart={(e) => onDragStart(e, true, false)} />
            <Grabber
                position={bottomRight}
                cursorChange={cursorChange}
                onDragStart={(e) => onDragStart(e, false, false)}
            />
            <Grabber position={topBottom} cursorChange={cursorChange} onDragStart={(e) => onDragStart(e, true, true)} />
            <Grabber
                position={bottomBottom}
                cursorChange={cursorChange}
                onDragStart={(e) => onDragStart(e, false, true)}
            />
        </Group>
    );
};

export default RadiusGrabbers;
